import React, { useState } from 'react'
import { Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import { withStyles } from '@mui/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { foldingPanelStyles } from './folding-panel.styles'

/**
 * @typedef {object} FoldingPanelComponentProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {boolean} [isExpanded=false] - Define el estado inicial del panel (expandido o contraído).
 * @property {boolean} [noPadding] - Si es `true`, elimina el padding del contenido del acordeón.
 * @property {string} title - El título que se muestra en la cabecera del panel plegable.
 * @property {React.ReactNode} children - El contenido que se muestra cuando el panel está expandido.
 */

/**
 * Un componente de panel plegable (acordeón) reutilizable que gestiona su propio estado de expansión.
 * 
 * Construido sobre el componente `Accordion` de Material-UI, proporciona una interfaz simple
 * para crear secciones colapsables con un título y contenido. El usuario puede expandir o contraer
 * el panel haciendo clic en la cabecera.
 * 
 * @component
 * @example
 * const MyPage = () => {
 *   return (
 *     <FoldingPanelComponent
 *       title="Información Adicional"
 *       isExpanded={true}
 *     >
 *       <p>Este contenido se puede mostrar u ocultar.</p>
 *       <p>Ideal para organizar información en secciones.</p>
 *     </FoldingPanelComponent>
 *   );
 * };
 * 
 * @example
 * // Panel sin padding interno
 * <FoldingPanelComponent
 *   title="Tabla de Datos"
 *   noPadding
 * >
 *   <DataTable data={tableData} />
 * </FoldingPanelComponent>
 * 
 * @param {FoldingPanelComponentProps} props - Las props del componente FoldingPanelComponent.
 */
export const FoldingPanelComponent = withStyles(foldingPanelStyles)(
  ({ classes, isExpanded = false, noPadding, ...props }) => {
    const [expanded, setExpanded] = useState(isExpanded)

    return (
      <div className={classes.panel}>
        <Accordion
          className={classes.folding}
          expanded={expanded}
          onChange={() => setExpanded(!expanded)}
        >
          <AccordionSummary
            className={classes.foldingHeader}
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography variant="h6">{props.title}</Typography>
          </AccordionSummary>
          <AccordionDetails style={noPadding ? { padding: 0 } : {}}>
            {props.children}
          </AccordionDetails>
        </Accordion>
      </div>
    )
  }
)
